import { Icon } from '../icon';
import React, { PropsWithChildren, Suspense, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/shared/appState';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { backofficeUrl } from '../../../shared/helpers';
import { useLastUsedTool } from '../../../shared/hooks';
import MdReportProblem from '../../../shared/icons/exclamation-triangle-solid.svg';
import { BackContainer, SelectedMenuItem } from './ContainerTrans';
import MenuItems from './ContainerTrans';
import { GetIcon } from './IconManager';
import { ItemMenu } from './Menu';
import { useSidebarMenuContext } from './SidebarMenuContext';
import SmallMenu from './SmallMenu';
import ToDoList from './ToDoList';
import TrialAlert from './TrialAlert';
import { AppLeftSidebarBottomFragment$key } from './__generated__/AppLeftSidebarBottomFragment.graphql';
import { AppLeftSidebarMainQuery } from './__generated__/AppLeftSidebarMainQuery.graphql';
import { AppLeftSidebarProductsWarningFragment$key } from './__generated__/AppLeftSidebarProductsWarningFragment.graphql';
import { isMenuItemSelected } from './helpers';
import {
    Body,
    ContainerIcon,
    ContainerLeft,
    ContainerLeftBottom,
    ContainerLeftTop,
    ContainerRight,
    Header,
    LinkSidebar,
    Linkcss,
    Main,
    Navbar,
    NavbarContainer,
    NavbarLi,
    Trial
} from './leftAppSidebarStyles';

export interface AppLeftSidebarProps {
    isMenuHidden: boolean;
}

const MenuProductWarning = styled.div`
    padding: 10px 20px;
    border-radius: 5px;
    background-color: #ffe1be;
`;

const MessageDetails = styled.div`
    margin-bottom: 10px;
`;

const MessageFooter = styled.button`
    ${Linkcss};
    background: none;
    border: 0;
    margin: 0;
    padding: 0;
    font-weight: bold;
    white-space: nowrap;
`;

const StyledWarningIcon = styled(MdReportProblem)`
    color: #f46200;
    font-size: 20px;
    margin-right: 10px;
`;

const MenuProductWarningWrapper = styled.div`
    padding: 10px;
`;

function getSelectedSubMenu(root: ItemMenu) {
    let selected = root;
    const findSelected = (item: ItemMenu) => {
        if (item.menu.length > 0) {
            for (const x of item.menu) {
                // Skip "home" because it's the root
                if (x.name.toLowerCase() === 'home') continue;

                const isSelected = isMenuItemSelected(x);
                if (isSelected && x.name.toLocaleLowerCase() === 'dam') {
                    findSelected(x);
                } else if (isSelected) {
                    selected = item; // The page is the parent
                    return;
                }

                findSelected(x);
            }
        }
    };

    findSelected(root);

    return selected;
}
const sidebarQuery = graphql`
    query AppLeftSidebarMainQuery($lastUsedTool: Int!) {
        treeMenu(lastUsedTool: $lastUsedTool)
        currentIAMUser {
            isIAMUser
            permissions
        }
    }
`;

export function AppLeftSidebar(props: AppLeftSidebarProps) {
    const { state } = useAppState();
    const { setLastUsedTool } = useLastUsedTool();
    const lastUsedTool = state.lastUsedTool;

    // Qui ho aggiunto inline il "child" che farà la query.
    // La query viene rieseguita con il nuovo valore ogni volta che cambio tool
    // ed in questo modo al cambio della key - lastUsedTool il menu viene smontato e rimontato

    const SidebarInner = () => {
        const data = useLazyLoadQuery<AppLeftSidebarMainQuery>(sidebarQuery, { lastUsedTool }, { fetchPolicy: 'store-and-network' });
        const rootMenu = JSON.parse(data.treeMenu) as ItemMenu;
        const [currentMenu, setCurrentMenu] = useState(getSelectedSubMenu(rootMenu));

        const iamUser = data.currentIAMUser.isIAMUser;
        const iamUserPermissions = data.currentIAMUser.permissions as string[];

        const { override } = useSidebarMenuContext();

        if (props.isMenuHidden) return null;

        // When a page provides an override, render its custom menu items
        if (override) {
            return (
                <Main id='main-left-sidebar'>
                    <Header />

                    <Body>
                        <ContainerLeft>
                            <ContainerLeftTop>
                                <NavbarContainer>
                                    <Navbar>
                                        <BackContainer onClick={override.onBackClick}>
                                            <Icon>{React.createElement(GetIcon('arrow_left_solid')!)}</Icon>
                                            <strong>{override.backLabel}</strong>
                                        </BackContainer>

                                        {override.items.map((item) => {
                                            const isSelected = item.key === override.selectedKey;
                                            return (
                                                <NavbarLi key={item.key} $isVisible>
                                                    <LinkSidebar
                                                        href='javascript:void(0)'
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            override.onItemClick(item.key);
                                                        }}
                                                    >
                                                        <ContainerIcon $isSelected={isSelected}>
                                                            {GetIcon(item.icon) && React.createElement(GetIcon(item.icon)!)}
                                                        </ContainerIcon>
                                                        {isSelected ? <SelectedMenuItem>{item.label}</SelectedMenuItem> : <span>{item.label}</span>}
                                                    </LinkSidebar>
                                                </NavbarLi>
                                            );
                                        })}
                                    </Navbar>
                                </NavbarContainer>
                            </ContainerLeftTop>

                            <ContainerLeftBottom>
                                <Suspense fallback={null}>
                                    <LeftSidebarBottom />
                                </Suspense>
                            </ContainerLeftBottom>
                        </ContainerLeft>

                        <ContainerRight $numOfShadow={1}>
                            <SmallMenu
                                homeMenu={rootMenu}
                                currentMenu={currentMenu}
                                goToMenu={setCurrentMenu}
                                onBackClick={() => setCurrentMenu(rootMenu)}
                                iamUser={iamUser}
                                iamUserPermissions={iamUserPermissions}
                            />
                        </ContainerRight>
                    </Body>
                </Main>
            );
        }

        return (
            <Main id='main-left-sidebar'>
                <Header />

                <Body>
                    <ContainerLeft>
                        <ContainerLeftTop>
                            <ProductNotPublishedWarning />

                            <MenuItems
                                homeMenu={rootMenu}
                                currentMenu={currentMenu}
                                goToMenu={setCurrentMenu}
                                onBackClick={() => {
                                    if (window.location.href.includes('DAM')) {
                                        setLastUsedTool('CUSTOMIZER'); // forzo il cambio tool AD 1
                                        window.location.href = 'Dashboard';
                                        return;
                                    }
                                    setCurrentMenu(rootMenu);
                                }}
                                iamUser={iamUser}
                                iamUserPermissions={iamUserPermissions}
                            />
                        </ContainerLeftTop>

                        <ContainerLeftBottom>
                            <Suspense fallback={null}>
                                <LeftSidebarBottom />
                            </Suspense>
                        </ContainerLeftBottom>
                    </ContainerLeft>

                    <ContainerRight $numOfShadow={1}>
                        <SmallMenu
                            homeMenu={rootMenu}
                            currentMenu={currentMenu}
                            goToMenu={setCurrentMenu}
                            onBackClick={() => setCurrentMenu(rootMenu)}
                            iamUser={iamUser}
                            iamUserPermissions={iamUserPermissions}
                        />
                    </ContainerRight>
                </Body>
            </Main>
        );
    };

    return <SidebarInner key={lastUsedTool} />;
}

const LeftSidebarBottom = () => {
    const { queryData } = useAppState();
    const query = useFragment<AppLeftSidebarBottomFragment$key>(
        graphql`
            fragment AppLeftSidebarBottomFragment on Query {
                user {
                    userID
                    email
                    name
                }
                testOrdersCount
                treeMenuReferringInfo {
                    daysOfTrial
                    firstNotPublishedProductID
                    hasFirstCustomizableProduct
                    hasPublishedProduct
                    hasStoreConnected
                    isAdministrator
                    isComposerEnabled
                    isExternalLinksHidden
                    isHelpWidgetHidden
                    isMenuHidden
                    isNoViewsPlan
                    isSubscriptionCanceled
                    isSubscriptionSubStatusBlocked
                    isToolbarHidden
                    isTrial
                    isVisibleSubscribePlan
                    noValidSubscriptionReason
                    planVersion
                    remoteUrl
                    sellerAppsumoMarketplaceID
                    subscriptionCancel
                    toolbarForceTools
                    urlComposer
                    userType
                }
            }
        `,
        queryData!
    );

    const isAdministrator = query.treeMenuReferringInfo.isAdministrator ?? false;
    const planVersion = query.treeMenuReferringInfo.planVersion ?? 2;
    const daysOfTrial = query.treeMenuReferringInfo.daysOfTrial;
    const isTrial = query.treeMenuReferringInfo.isTrial ?? false;
    const userId = query.user.userID;
    const isVisibleSubscribePlan = query.treeMenuReferringInfo.isVisibleSubscribePlan ?? false;
    const hasFirstCustomizableProduct = query.treeMenuReferringInfo.hasFirstCustomizableProduct ?? false;
    const hasStoreConnected = query.treeMenuReferringInfo.hasStoreConnected ?? false;
    const hasPlaceTestOrder = query.testOrdersCount > 0;

    return (
        <>
            {!isAdministrator && planVersion === 2 && (
                <ToDoList
                    hasStoreConnected={hasStoreConnected}
                    hasFirstCustomizableProduct={hasFirstCustomizableProduct}
                    hasPlaceTestOrder={hasPlaceTestOrder}
                    userId={userId}
                ></ToDoList>
            )}

            {isTrial && (
                <Trial $isTrial={isTrial}>
                    <TrialAlert daysOfTrial={daysOfTrial} isVisibleSubscribePlan={isVisibleSubscribePlan} />
                </Trial>
            )}
        </>
    );
};

const ProductNotPublishedWarning = () => {
    const { queryData } = useAppState();
    const query = useFragment<AppLeftSidebarProductsWarningFragment$key>(
        graphql`
            fragment AppLeftSidebarProductsWarningFragment on Query {
                treeMenuReferringInfo {
                    firstNotPublishedProductID
                    hasFirstCustomizableProduct
                    hasPublishedProduct
                }
            }
        `,
        queryData!
    );

    const navigate = useNavigate();
    const hasPublishedProducts = query.treeMenuReferringInfo.hasPublishedProduct ?? false;
    const firstNotPublishedProductID = query.treeMenuReferringInfo.firstNotPublishedProductID;

    const [hasNotPublishedProducts, setHasNotPublishedProducts] = useState(!hasPublishedProducts);
    const [firstNotPublishedProductId, setFirstNotPublishedProductId] = useState<number | null | undefined>(firstNotPublishedProductID);

    (window as any).isMenuNotPublishedProductsWarningVisible = hasNotPublishedProducts;
    (window as any).setMenuNotPublishedProductsWarningVisible = (visible: boolean, productId: number | null | undefined) => {
        setHasNotPublishedProducts(visible);

        if (productId) setFirstNotPublishedProductId(productId);
    };

    if (!hasNotPublishedProducts) return null;

    return (
        <MenuProductWarningWrapper>
            <MenuProductWarning>
                <MessageDetails>You still have to finish publishing the first product to your store.</MessageDetails>
                <MessageFooter onClick={() => navigate(backofficeUrl(`/preview/customizer/${firstNotPublishedProductId}`))}>Publish it now</MessageFooter>
            </MenuProductWarning>
        </MenuProductWarningWrapper>
    );
};

const LeftAppSidebarSkeleton = () => {
    return (
        <Main>
            <Body>
                <ContainerLeft>
                    <ContentLoader
                        speed={2}
                        width='100%'
                        height='500px'
                        backgroundColor={theme.app.backgroundColor}
                        foregroundColor={theme.skeleton.foregroundColor}
                    >
                        <rect x='10px' y={'20px'} rx='10' ry='10' width='90%' height='27' />
                        <rect x='10px' y={'60px'} rx='10' ry='10' width='90%' height='27' />
                        <rect x='10px' y={'100px'} rx='10' ry='10' width='90%' height='27' />
                        <rect x='10px' y={'140px'} rx='10' ry='10' width='90%' height='27' />
                        <rect x='10px' y={'180px'} rx='10' ry='10' width='90%' height='27' />
                        <rect x='10px' y={'220px'} rx='10' ry='10' width='90%' height='27' />
                    </ContentLoader>
                </ContainerLeft>
            </Body>
        </Main>
    );
};

interface LeftAppSidebarProps {
    isMenuHidden: boolean;
}

const LeftAppSidebar = (props: PropsWithChildren<LeftAppSidebarProps>) => {
    return (
        <Suspense fallback={!props.isMenuHidden ? <LeftAppSidebarSkeleton /> : null}>
            <AppLeftSidebar isMenuHidden={props.isMenuHidden} />
        </Suspense>
    );
};

export default LeftAppSidebar;
